import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Phone } from "lucide-react";
import { HouseMark } from "../components/landing/SiteNav";
import { API, formatApiError } from "../lib/contact";

const http = axios.create({ baseURL: API, withCredentials: true });
const input = "w-full border-2 border-navy bg-ivory px-4 py-3 text-lg focus:outline-none focus:border-coral";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="max-w-md mx-auto mt-24 border-2 border-navy p-8 grid gap-5 bg-ivory">
      <h1 className="disp uppercase text-3xl">Megkeresések – belépés</h1>
      <label className="grid gap-2 font-semibold">E-mail
        <input data-testid="admin-email-input" type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} required />
      </label>
      <label className="grid gap-2 font-semibold">Jelszó
        <input data-testid="admin-password-input" type="password" className={input} value={password} onChange={(e) => setPassword(e.target.value)} required />
      </label>
      {error && <p data-testid="admin-login-error" className="text-burgundy font-semibold">{error}</p>}
      <button data-testid="admin-login-button" className="bg-coral text-coral-ink py-4 text-lg font-semibold hover:bg-coral-deep transition-colors">Belépés</button>
    </form>
  );
};

const InquiryRow = ({ item, onToggle }) => (
  <li data-testid={`admin-inquiry-${item.id}`} className="grid md:grid-cols-[1fr_auto] gap-4 py-6 items-center">
    <div>
      <p className="text-xl font-semibold">{item.name} <span className="text-base font-normal text-navy/70">· {item.city || "–"}</span></p>
      <a href={`tel:${item.phone}`} className="inline-flex items-center gap-2 text-lg tab text-burgundy font-semibold"><Phone className="h-4 w-4" />{item.phone}</a>
      <p className="text-base text-navy/75">{item.interest} · {new Date(item.created_at).toLocaleString("hu-HU")}</p>
    </div>
    <button
      data-testid={`admin-toggle-${item.id}`}
      onClick={() => onToggle(item)}
      className={`px-5 py-3 font-semibold border-2 transition-colors ${item.status === "called" ? "border-sage bg-sage text-ivory" : "border-navy hover:bg-navy hover:text-ivory"}`}
    >
      {item.status === "called" ? "Visszahívva ✓" : "Megjelölés: visszahívva"}
    </button>
  </li>
);

export default function Admin() {
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const { data } = await http.get("/inquiries");
    setItems(data);
  }, []);

  useEffect(() => {
    http.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);
  useEffect(() => { if (user) load().catch(() => toast.error("Nem sikerült betölteni.")); }, [user, load]);

  const toggle = async (item) => {
    const status = item.status === "called" ? "new" : "called";
    await http.patch(`/inquiries/${item.id}`, { status });
    setItems((xs) => xs.map((x) => (x.id === item.id ? { ...x, status } : x)));
  };
  const logout = async () => { await http.post("/auth/logout"); setUser(false); };

  if (user === null) return <div className="min-h-screen bg-ivory" />;
  if (!user) return <div className="min-h-screen bg-ivory px-6"><Login onLogin={setUser} /></div>;

  return (
    <div className="min-h-screen bg-ivory text-navy">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between gap-4 border-b-2 border-navy pb-6">
          <div className="flex items-center gap-3"><HouseMark /><h1 className="disp uppercase text-2xl md:text-3xl">Beérkezett megkeresések</h1></div>
          <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 font-semibold"><LogOut className="h-5 w-5" /> Kilépés</button>
        </div>
        <p data-testid="admin-count" className="mt-6 text-lg">{items.length} megkeresés</p>
        <ul className="divide-y-2 divide-navy/15">
          {items.map((it) => <InquiryRow key={it.id} item={it} onToggle={toggle} />)}
        </ul>
        {items.length === 0 && <p className="py-12 text-lg text-navy/70">Még nem érkezett megkeresés.</p>}
      </div>
    </div>
  );
}
